import React from 'react';
import styled from '@emotion/styled';
import { Box, Flex } from '../../Rebass';
import Text from '../../Text';
import Icon from '../../Icon';
import theme from '../../../theme';

const HiddenInput = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const NumberContainer = styled(Flex)`
  display: flex;
  margin-bottom: 1px;
  cursor: pointer;
  position: relative;
  ${(props) =>
    props.disabled &&
    `
      cursor: not-allowed;
      opacity: 0.4; 
    `}
  text-transform: none;
`;

const StyledIcon = styled(Icon)<{ color?: string }>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: ${({ color }) => color ?? theme.colors.brandBrown};
  transition: color ${theme.speed.default} ease;
`;

const StyledControl = styled(Box)`
  position: relative;
  height: 30px;
  width: 30px;
  border-radius: 50%;
  border: 1px solid ${({ color }) => color ?? theme.colors.brandBrown};
  transition: background-color ${theme.speed.default} ease;

  &:hover {
    background-color: ${({ color }) => color ?? theme.colors.brandBrown};

    .icon {
      color: ${theme.colors.baseWhite};
    }
  }
`;

const CustomNumber = ({ value, increment, decrement, color }) => {
  return (
    <Flex justifyContent="space-between" alignItems="center">
      <StyledControl
        data-testid="number-decrement"
        onClick={decrement}
        color={color}
      >
        <StyledIcon name="minus" color={color}></StyledIcon>
      </StyledControl>
      <Box mx={[1]}>
        <Text size="small" color="textDark">
          {value}
        </Text>
      </Box>
      <StyledControl
        data-testid="number-increment"
        onClick={increment}
        color={color}
      >
        <StyledIcon name="plus" color={color}></StyledIcon>
      </StyledControl>
    </Flex>
  );
};

interface NumberProps {
  disabled?: boolean;
  label?: string;
  description?: string;
  initialValue?: number;
  orientation: 'left' | 'right';
  min?: number;
  max?: number;
  value: number;
  onIncrement: () => void;
  onDecrement: () => void;
  color?: string;
  error?: string;
}

const Number = ({
  disabled,
  label,
  description,
  initialValue = 0,
  orientation = 'left',
  min = 0,
  max = 100,
  value,
  onIncrement,
  onDecrement,
  color,
  error,
  ...props
}: NumberProps) => {
  return (
    <Box>
      <NumberContainer
        size="small"
        disabled={disabled}
        as="label"
        alignItems="center"
        justifyContent="space-between"
        flexDirection={orientation === 'left' ? 'row' : 'row-reverse'}
        mb={error ? [1] : [0]}
      >
        <HiddenInput
          type="number"
          disabled={disabled}
          value={value}
          {...props}
        />
        <CustomNumber
          value={value}
          increment={() => (value < max ? onIncrement() : null)}
          decrement={() => (value > min ? onDecrement() : null)}
          color={error ? theme.colors.statusError : color}
        />
        <Flex flexDirection="column">
          <Text size="small" color="textDark">
            {label}
          </Text>
          {description && (
            <Text size="tiny" color="brandBrown">
              {description}
            </Text>
          )}
        </Flex>
      </NumberContainer>
      {error && (
        <Text color="statusError" size="small">
          {error}
        </Text>
      )}
    </Box>
  );
};

export default Number;
